export type ProviderId = 'openverse' | 'pixabay';

export type ProviderState = {
  providers: Record<ProviderId, boolean>;
  configuredProviders: Record<ProviderId, boolean>;
  hasPixabayKey: boolean;
  customerDirectSearch: boolean;
};

export type StockImageResult = {
  id: string;
  provider: ProviderId;
  title: string;
  thumb: string;
  preview?: string;
  attribution: string;
  license: string;
  licenseUrl?: string;
  sourceUrl?: string;
  width?: number;
  height?: number;
  local?: boolean;
};

export type ImportedStockItem = {
  title: string;
  url: string;
  file_name: string;
  thumb: string;
  medium: string;
  original: string;
  file_type: 'image';
  size?: number;
  stockProvider: ProviderId;
  stockAttribution: string;
  stockLicense: string;
  stockLicenseUrl?: string;
  stockSourceUrl?: string;
  stockWidth?: number;
  stockHeight?: number;
};

export type ApiEnvelope<T> = { error: number; msg?: string; data?: T };

export type StockRateLimitData = {
  provider: 'pixabay';
  code: 'rate_limited';
  retryAfter: number;
};

export class StockApiError extends Error {
  constructor(message: string, public status: number, public data?: unknown) {
    super(message);
    this.name = 'StockApiError';
  }
}

export function stockRateLimitData(error: unknown): StockRateLimitData | null {
  if (!(error instanceof StockApiError) || !error.data || typeof error.data !== 'object') return null;
  const data = error.data as Partial<StockRateLimitData>;
  if (data.provider !== 'pixabay' || data.code !== 'rate_limited') return null;
  return { provider: 'pixabay', code: 'rate_limited', retryAfter: Math.max(1, Number(data.retryAfter) || 60) };
}

export function designerBaseUrl(context?: { config?: Record<string, unknown> }): string {
  const raw = String(context?.config?.veyopUrl ?? '').trim();
  if (raw) {
    try {
      const resolved = new URL(raw, String(context?.config?.siteUrl ?? window.location.origin)).toString();
      return resolved.endsWith('/') ? resolved : `${resolved}/`;
    } catch {
      // Fall through.
    }
  }
  const configEl = document.getElementById('designer-config');
  try {
    const config = JSON.parse(configEl?.textContent || '{}') as { veyopUrl?: string; siteUrl?: string };
    if (config.veyopUrl) {
      const resolved = new URL(config.veyopUrl, config.siteUrl || window.location.origin).toString();
      return resolved.endsWith('/') ? resolved : `${resolved}/`;
    }
  } catch {
    // Fall through.
  }
  const base = (window as Window & { VeyopHostConfig?: { baseUrl?: string } }).VeyopHostConfig?.baseUrl || window.location.origin;
  return `${base.replace(/\/$/, '')}/`;
}

export function adminBaseUrl(): string {
  const host = document.querySelector('veyop-admin');
  const base = (host?.getAttribute('base-url') || '').replace(/\/+$/, '');
  const hostBase = (window as Window & { VeyopHostConfig?: { baseUrl?: string } }).VeyopHostConfig?.baseUrl;
  return base || hostBase || window.location.origin;
}

export function csrfToken(context?: { config?: Record<string, unknown> }): string {
  const fromContext = String(context?.config?.csrfToken ?? '').trim();
  if (fromContext) return fromContext;
  const host = document.querySelector('veyop-admin');
  const fromHost = host?.getAttribute('csrf-token') || '';
  if (fromHost) return fromHost;
  return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content?.trim() || '';
}

export function apiToken(context?: { config?: Record<string, unknown> }): string {
  const fromContext = String(context?.config?.apiNonce ?? '').trim();
  if (fromContext) return fromContext;
  const host = document.querySelector('veyop-admin');
  return host?.getAttribute('csrf-token') || '';
}

export async function request<T>(base: string, path: string, options: RequestInit = {}): Promise<T> {
  const portable = (window as Window & { Veyop?: { http?: { request<R>(path: string, options?: RequestInit): Promise<R> } } }).Veyop?.http;
  if (portable) return portable.request<T>(path, options);
  const response = await fetch(`${base.replace(/\/+$/, '')}${path}`, {
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      ...(options.body ? { 'Content-Type': 'application/json' } : {}),
      ...(options.headers ?? {}),
    },
    ...options,
  });
  const payload = await response.json().catch(() => ({ error: response.status, msg: response.statusText })) as ApiEnvelope<T>;
  if (!response.ok || payload.error) throw new StockApiError(payload.msg || `Request failed (${response.status})`, response.status, payload.data);
  return payload.data as T;
}
